import React, { useState, useEffect } from 'react';
import './Header.css';
import logoImage from './images/logo/logo-last.svg'; // Import the logo image from the correct path
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from './LanguageSelector';

const Header = () => {
  const [scroll, setScroll] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [borderVisible, setBorderVisible] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 80) {
        setScroll(true);
        // Add a slight delay to the border appearance for a subtle effect
        setTimeout(() => {
          setBorderVisible(true);
        }, 150);
      } else {
        setScroll(false);
        setBorderVisible(false);
      }
      
      // Check which section is currently in view
      const sections = ['about', 'artist', 'services', 'contact'];
      const scrollPosition = window.scrollY + 100;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 80,
        behavior: 'smooth'
      });
    }
    if (mobileMenuOpen) {
      toggleMobileMenu();
    }
  };

  return (
    <header 
      className={`header ${scroll ? 'header-scroll' : ''} ${borderVisible ? 'border-visible' : ''}`}
      data-section={activeSection}
    >
      {/* Mobile Menu Toggle */}
      <div onClick={toggleMobileMenu} className={`mobile-menu-toggle ${mobileMenuOpen ? 'active' : ''}`}>
        <span></span>
        <span></span>
        <span></span>
      </div>
      
      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'active' : ''}`}>
        {/* Mobile Menu Logo */}
        
        <ul className="mobile-menu-nav">
          <li style={{"--item-index": 0}}>
            <a href="#about" onClick={() => scrollToSection('about')} className={activeSection === 'about' ? 'active' : ''}>
              {t('header.about')}
            </a>
          </li>
          <li style={{"--item-index": 1}}>
            <a href="#artist" onClick={() => scrollToSection('artist')} className={activeSection === 'artist' ? 'active' : ''}>
              {t('header.artist')}
            </a>
          </li>
          <li style={{"--item-index": 2}}>
            <a href="#services" onClick={() => scrollToSection('services')} className={activeSection === 'services' ? 'active' : ''}>
              {t('header.services')}
            </a>
          </li>
          <li style={{"--item-index": 3}}>
            <a href="#contact" onClick={() => scrollToSection('contact')} className={activeSection === 'contact' ? 'active' : ''}>
              {t('header.contact')}
            </a>
          </li>
        </ul>
        
        {/* Mobile Social Icons */}
        <div className="mobile-social-icons">
          <a href="https://www.instagram.com/kellink_tattoo/profilecard/?igsh=MThoeTcxZzdldGV4cA==" className="mobile-social-icon" target="_blank" rel="noopener noreferrer">
            <i className="fa-brands fa-instagram"></i>
          </a>
          <a href="https://www.facebook.com/share/1DYWFGYEgg/?mibextid=wwXIfr" className="mobile-social-icon" target="_blank" rel="noopener noreferrer">
            <i className="fa-brands fa-facebook-f"></i>
          </a>
          <a href="https://www.tiktok.com/" className="mobile-social-icon" target="_blank" rel="noopener noreferrer">
            <i className="fa-brands fa-tiktok"></i>
          </a>
          <div className="mobile-language-selector">
            <LanguageSelector />
          </div>
        </div>
      </div>

      <div className="container">
        {/* Social icons */}
        <div className="social-icons">
          <a href="https://www.facebook.com/share/1DYWFGYEgg/?mibextid=wwXIfr" className="social-icon" target="_blank" rel="noopener noreferrer">
            <i className="fa-brands fa-facebook-f"></i>
          </a>
          <a href="https://www.instagram.com/kellink_tattoo/profilecard/?igsh=MThoeTcxZzdldGV4cA==" className="social-icon" target="_blank" rel="noopener noreferrer">
            <i className="fa-brands fa-instagram"></i>
          </a>
        </div>

        {/* Desktop Navigation */}
        <nav className="main-nav">
          <ul>
            <li>
              <a 
                href="#about" 
                onClick={() => scrollToSection('about')}
                className={activeSection === 'about' ? 'active' : ''}
              >
                {t('header.about')}
              </a>
            </li>
            <li>
              <a 
                href="#artist" 
                onClick={() => scrollToSection('artist')}
                className={activeSection === 'artist' ? 'active' : ''}
              >
                {t('header.artist')}
              </a>
            </li>
          </ul>

          {/* Logo */}
          <div className="logo">
            <a href="#home" onClick={() => scrollToSection('home')}>
              <img src={logoImage} alt="Kellink Tattoo" className="logo-image" />
            </a>
          </div>

          <ul>
            <li>
              <a 
                href="#services" 
                onClick={() => scrollToSection('services')}
                className={activeSection === 'services' ? 'active' : ''}
              >
                {t('header.services')}
              </a>
            </li>
            <li>
              <a 
                href="#contact" 
                onClick={() => scrollToSection('contact')}
                className={activeSection === 'contact' ? 'active' : ''}
              >
                {t('header.contact')}
              </a>
            </li>
          </ul>
        </nav>

        {/* Contact button and Language Selector */}
        <div className="contact-btn">
          <LanguageSelector />
        </div>
      </div>
    </header>
  );
};

export default Header; 